import { Loader2, CheckCircle, Circle, MapPin, Database, Brain, Sparkles } from "lucide-react";
import { getRandomFact } from "@/data/funFacts";
import { useState, useEffect } from "react";

interface LoadingStep {
  label: string;
  status: "pending" | "active" | "complete";
  icon: React.ReactNode;
}

interface LoadingProgressProps {
  currentStep: number;
  address?: string;
  estimatedTime?: number;
}

const defaultSteps: Omit<LoadingStep, "status">[] = [
  { label: "Finding your address", icon: <MapPin className="h-4 w-4" /> },
  { label: "Gathering neighborhood data", icon: <Database className="h-4 w-4" /> },
  { label: "Analyzing with AI", icon: <Brain className="h-4 w-4" /> },
  { label: "Generating insights", icon: <Sparkles className="h-4 w-4" /> },
];

export const LoadingProgress = ({ 
  currentStep, 
  address,
  estimatedTime = 20 
}: LoadingProgressProps) => {
  const [funFact, setFunFact] = useState<string>("");
  const [secondsRemaining, setSecondsRemaining] = useState(estimatedTime);

  // Get a random fun fact on mount
  useEffect(() => {
    setFunFact(getRandomFact());
  }, []);

  // Countdown timer
  useEffect(() => {
    if (secondsRemaining <= 0) return;
    
    const timer = setInterval(() => {
      setSecondsRemaining(prev => Math.max(0, prev - 1));
    }, 1000);

    return () => clearInterval(timer);
  }, [secondsRemaining]);

  const steps: LoadingStep[] = defaultSteps.map((step, index) => ({
    ...step,
    status: index < currentStep ? "complete" : index === currentStep ? "active" : "pending",
  }));

  return (
    <div className="w-full max-w-md mx-auto text-center space-y-8 py-12 px-4 animate-fade-in">
      {/* Main loader animation */}
      <div className="relative">
        <div className="w-20 h-20 mx-auto rounded-full bg-primary/10 flex items-center justify-center">
          <Loader2 className="h-10 w-10 animate-spin text-primary" />
        </div>
        {/* Pulse rings */}
        <div className="absolute inset-0 w-20 h-20 mx-auto rounded-full bg-primary/5 animate-ping" style={{ animationDuration: '2s' }} />
      </div>

      {/* Address being analyzed */}
      {address && (
        <div className="space-y-1">
          <p className="text-sm text-muted-foreground">Analyzing</p>
          <p className="font-semibold text-foreground truncate px-4">{address}</p>
        </div>
      )}

      {/* Progress steps */}
      <div className="space-y-3 text-left bg-card border border-border rounded-xl p-4">
        {steps.map((step, i) => (
          <div 
            key={i} 
            className={`flex items-center gap-3 transition-all duration-300 ${
              step.status === "pending" ? "opacity-40" : "opacity-100"
            }`}
          >
            {/* Status icon */}
            <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center transition-colors ${
              step.status === "complete" 
                ? "bg-[hsl(var(--status-positive))]/10 text-[hsl(var(--status-positive))]" 
                : step.status === "active"
                ? "bg-primary/10 text-primary"
                : "bg-muted text-muted-foreground"
            }`}>
              {step.status === "complete" ? (
                <CheckCircle className="h-4 w-4" />
              ) : step.status === "active" ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Circle className="h-4 w-4" />
              )}
            </div>
            
            {/* Step label */}
            <span className={`text-sm ${
              step.status === "complete" 
                ? "text-muted-foreground line-through" 
                : step.status === "active"
                ? "text-foreground font-medium"
                : "text-muted-foreground"
            }`}>
              {step.label}
            </span>
          </div>
        ))}
      </div>

      {/* Fun fact */}
      {funFact && (
        <div className="p-4 bg-primary/5 rounded-xl border border-primary/10">
          <p className="text-sm text-foreground">
            <span className="font-semibold text-primary">💡 Did you know?</span>
            <br />
            <span className="text-muted-foreground">{funFact}</span>
          </p>
        </div>
      )}

      {/* Time remaining */}
      <p className="text-sm text-muted-foreground animate-pulse-subtle">
        {secondsRemaining > 0 
          ? `~${secondsRemaining} seconds remaining`
          : "Almost there..."
        }
      </p>
    </div>
  );
};

export default LoadingProgress;


